/**
 * StatusPipeline — Mission Phase Advance
 *
 * Visual one-step-at-a-time status advance control.
 * Replaces the plain SelectInput + SaveButton in the order detail view.
 *
 * Role rules:
 *   staff   → sees placed + packed only; capped at packed
 *   manager / admin / owner → full pipeline, one step at a time, no skip, no revert
 *   marketing / skc / customer → read-only display
 */
import React, { useState } from 'react';
import { Box, Stack, Typography, Chip, Collapse, Button, alpha, keyframes } from '@mui/material';
import { ArrowForwardIos as ArrowIcon, CheckCircle as DoneIcon } from '@mui/icons-material';
import { useMissionTheme } from '../theme';
import type { MissionColorTokens } from '../theme';
import type { UserRole } from '../../../context/PermissionContext';

const pulsing = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.97); }
`;

const FULL_PIPELINE = [
  { key: 'placed',     label: 'PLACED'     },
  { key: 'packed',     label: 'PACKED'     },
  { key: 'dispatched', label: 'DISPATCHED' },
  { key: 'tracking',   label: 'TRACKING'   },
  { key: 'arrived',    label: 'ARRIVED'    },
  { key: 'delivered',  label: 'DELIVERED'  },
] as const;

type StatusKey = typeof FULL_PIPELINE[number]['key'];

function statusColor(key: StatusKey, mc: MissionColorTokens): string {
  const map: Record<StatusKey, string> = {
    placed:     mc.info,
    packed:     mc.success,
    dispatched: mc.secondary,
    tracking:   mc.warning,
    arrived:    mc.gold,
    delivered:  mc.success,
  };
  return map[key] ?? mc.textMuted;
}

interface StatusPipelineProps {
  currentStatus: string;
  role: UserRole | null;
  /** Called when user confirms advance. Parent owns the useUpdate call. */
  onAdvance: (nextStatus: string) => void;
  /** Whether a save is in flight */
  saving?: boolean;
}

export default function StatusPipeline({ currentStatus, role, onAdvance, saving = false }: StatusPipelineProps) {
  const { mc } = useMissionTheme();
  const [pendingStatus, setPendingStatus] = useState<StatusKey | null>(null);

  const canOperate = role === 'owner' || role === 'admin' || role === 'staff';

  // Every operating role sees the whole pipeline: which staff may open Mission Control is
  // the page allow-list, not the role (the former manager/staff split is gone, 23 Oct 2026).
  const visiblePipeline = FULL_PIPELINE;

  const currentIndex = FULL_PIPELINE.findIndex(s => s.key === currentStatus);
  const nextStep = FULL_PIPELINE[currentIndex + 1] as typeof FULL_PIPELINE[number] | undefined;

  const nextVisible = nextStep;

  const handleChipClick = (step: typeof FULL_PIPELINE[number]) => {
    if (!canOperate) return;
    if (step.key !== nextVisible?.key) return;
    setPendingStatus(step.key);
  };

  const handleConfirm = () => {
    if (!pendingStatus) return;
    onAdvance(pendingStatus);
    setPendingStatus(null);
  };

  return (
    <Box>
      {/* Pipeline chips */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={0.5}
        sx={{ mb: 1.5 }}
      >
        {visiblePipeline.map((step, i) => {
          const isCurrent = step.key === currentStatus;
          const isNext = step.key === nextVisible?.key;
          const isPast = FULL_PIPELINE.findIndex(s => s.key === step.key) < currentIndex;
          const isPending = step.key === pendingStatus;
          const color = statusColor(step.key as StatusKey, mc);

          return (
            <React.Fragment key={step.key}>
              <Chip
                label={step.label}
                size="small"
                icon={isPast ? <DoneIcon style={{ fontSize: 14 }} /> : undefined}
                onClick={() => handleChipClick(step)}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  letterSpacing: '0.06em',
                  cursor: isNext && canOperate ? 'pointer' : 'default',
                  // Current: coloured border + subtle glow
                  ...(isCurrent && {
                    bgcolor: alpha(color, 0.12),
                    color,
                    border: `1px solid ${alpha(color, 0.5)}`,
                    boxShadow: `0 0 12px ${alpha(color, 0.25)}`,
                  }),
                  // Next: amber pulse invite
                  ...(isNext && canOperate && !isPending && {
                    bgcolor: alpha(mc.warning, 0.08),
                    color: mc.warning,
                    border: `1px solid ${alpha(mc.warning, 0.4)}`,
                    animation: `${pulsing} 2s ease-in-out infinite`,
                    '&:hover': {
                      bgcolor: alpha(mc.warning, 0.15),
                      boxShadow: `0 0 14px ${alpha(mc.warning, 0.3)}`,
                    },
                  }),
                  // Pending confirmation
                  ...(isPending && {
                    bgcolor: alpha(mc.accent, 0.15),
                    color: mc.accent,
                    border: `1px solid ${mc.accent}`,
                    boxShadow: `0 0 16px ${mc.accentGlow}`,
                  }),
                  // Past: muted with checkmark
                  ...(isPast && {
                    bgcolor: alpha(color, 0.06),
                    color: alpha(color, 0.55),
                    border: `1px solid ${alpha(color, 0.2)}`,
                  }),
                  // Future (not next): dim
                  ...(!isCurrent && !isNext && !isPast && !isPending && {
                    bgcolor: 'transparent',
                    color: mc.textMuted,
                    border: `1px solid ${alpha(mc.border, 0.4)}`,
                  }),
                  transition: 'all 0.2s ease',
                }}
              />
              {/* Arrow connector — skip after last item */}
              {i < visiblePipeline.length - 1 && (
                <ArrowIcon sx={{ fontSize: 12, color: mc.textMuted, opacity: 0.4 }} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>

      {/* Confirm bar — slides in when a step is pending */}
      <Collapse in={!!pendingStatus} unmountOnExit>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: 1,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: alpha(mc.accent, 0.06),
            border: `1px solid ${alpha(mc.accent, 0.3)}`,
          }}
        >
          <Typography variant="body2" sx={{ color: mc.textSecondary, fontSize: '0.82rem' }}>
            Advance to{' '}
            <Box component="span" sx={{ color: mc.accent, fontWeight: 700 }}>
              {pendingStatus?.toUpperCase()}
            </Box>
            ?
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => setPendingStatus(null)}
              disabled={saving}
              sx={{ color: mc.textMuted, textTransform: 'none', fontSize: '0.78rem' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirm}
              disabled={saving}
              sx={{
                bgcolor: mc.accent,
                color: '#fff',
                textTransform: 'none',
                fontSize: '0.78rem',
                fontWeight: 700,
                boxShadow: `0 0 12px ${mc.accentGlow}`,
                '&:hover': { bgcolor: mc.error, boxShadow: `0 0 20px ${mc.accentGlow}` },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              {saving ? 'Saving…' : 'Confirm'}
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
}
